import type { ComponentType } from "react";
import { DisabledActionMenuItem } from "@/components/assurance/disabled-action-menu-item";
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export type ActionMenuItemDef = {
  key: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  onSelect: () => void;
  hidden?: boolean;
  disabledReason?: string;
};

export type ActionMenuGroup = {
  label?: string;
  items: ActionMenuItemDef[];
};

const ActionMenuItem = ({ icon: Icon, label, onSelect, disabledReason }: Omit<ActionMenuItemDef, "key" | "hidden">) => {
  if (disabledReason) {
    return <DisabledActionMenuItem icon={Icon} label={label} tooltip={disabledReason} />;
  }
  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className="flex items-center gap-2 py-2"
    >
      <Icon className="w-4 h-4 text-fg-quaternary shrink-0" />
      <span className="text-secondary font-semibold">{label}</span>
    </DropdownMenuItem>
  );
};

/**
 * Renders `<DropdownMenuContent>` children grouped into labeled sections, dropping any item
 * whose `hidden` is true and any group left with zero visible items — a group that only holds
 * a flag-gated item disappears entirely when that flag is off, rather than showing an empty label.
 */
export const GroupedActionMenu = ({ groups }: { groups: ActionMenuGroup[] }) => {
  const visibleGroups = groups
    .map((group) => ({ ...group, items: group.items.filter((item) => !item.hidden) }))
    .filter((group) => group.items.length > 0);

  return (
    <>
      {visibleGroups.map((group, index) => (
        <div key={group.label ?? `group-${index}`}>
          {index > 0 && <DropdownMenuSeparator />}
          {group.label && <DropdownMenuLabel className="text-quaternary">{group.label}</DropdownMenuLabel>}
          {group.items.map((item) => (
            <ActionMenuItem
              key={item.key}
              icon={item.icon}
              label={item.label}
              onSelect={item.onSelect}
              disabledReason={item.disabledReason}
            />
          ))}
        </div>
      ))}
    </>
  );
};
